// src/views/kiddo-games/drag-letters/KiddoDragLettersView.tsx
// Ułóż słowa Easy — kopia src/views/drag-letters/DragLettersView.tsx, rozwijana osobno.
// Oryginał z Regular usunięty 22.10.2026 — jest w historii gita.
// Różnice: własne style i komponenty, następna gra z grupy Easy.
import React, {
  memo, useEffect, useState, useCallback, useRef, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Spin, Result } from 'antd';
import classes from './KiddoDragLettersView.module.less';
import FixWidthContent from '../../../components/FixWidthContent';
import withTagManager from '../../../hooks/withTagManager';
import InfoButton from '../../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { getActiveList, getUserData } from '../../auth/slice/selectors';
import useGoToRoute from '../../../hooks/useGoToRoute';
import { Path } from '../../../constants/Path';
import EndGameInfo from '../../../components/EndGameInfo/EndgameInfo';
import { RightSideButtons } from '../../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../../components/UnsplashCredits/UnsplashCredits';
import { EditTermButton } from '../../../components/EditTermModal/EditTermButton';
import { Roles } from '../../../constants/Roles';
import { DraggableLetter } from './components/DraggableLetter';
import { LetterDropZone } from './components/LetterDropZone';
import { FixedCharZone } from './components/FixedCharZone';
import KiddoSmiley from '../../../components/KiddoSmiley/KiddoSmiley';
import { ReactComponent as Repeat } from '../../../assets/icons/games/kiddo/refresh-ccw-rec.svg';
import { ReactComponent as Arrow } from '../../../assets/icons/games/writing/arrow-right-rec.svg';
import { ReactComponent as Sound } from '../../../assets/icons/games/find/volume-high.svg';
import { cleanTextForTTS } from '../../../utils/textBracketSanitize';
import { splitPhrase } from '../../../utils/splitPhrase';
import { getListWordId } from '../../../utils/gameWords';
import useAudioPlayer from '../../../hooks/useAudioPlayer';
import { BASE_API_URL } from '../../../services/ApiService';
import WordCounter from '../../../components/WordCounter';
import { EditListButton } from '../../../components/EditListButton/EditListButton';
import useActivityTracking from '../../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../../services/activity';

interface LetterState {
  letter: string;
  originalIndex: number;
  id: string;
  isPlaced: boolean;
  placedInSlot: number | null;
}

enum GameStage {
  PLAYING = 'playing',
  FEEDBACK = 'feedback',
  GAME_END = 'end',
}

export const KiddoDragLettersView = memo(() => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const goToHome = useGoToRoute(Path.HOME);
  const goToNext = useGoToRoute(Path.KIDDO_WRITING);

  const hasValidData = activeList && activeList.words && activeList.words.length > 0;

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  useActivityTracking(ActivityGame.DRAG_LETTERS_EASY);

  // Kopia słów (źródło do losowania separatorów)
  const wordsCopy = useMemo(
    () => (hasValidData ? activeList.words.map((w: any) => ({ ...w })) : []),
    [hasValidData, activeList],
  );

  const [currentWords, setCurrentWords] = useState(() => {
    if (!hasValidData) return [];
    const wordsCopyLocal = [...activeList.words];

    return wordsCopyLocal.sort(() => 0.5 - Math.random());
  });

  const [wordIndex, setWordIndex] = useState<number>(0);
  const [gameStage, setGameStage] = useState<GameStage>(GameStage.PLAYING);
  const [isLoading, setIsLoading] = useState(true);
  const [letters, setLetters] = useState<LetterState[]>([]);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackInfo, setFeedbackInfo] = useState<string | React.ReactNode>('');
  const [tryAgainButton, setTryAgainButton] = useState(false);
  const [hadIncorrectAttempt, setHadIncorrectAttempt] = useState(false);
  // Słowa listy ułożone dobrze (pominięte przez SKIP tu nie trafiają) — od ich liczby zależy gwiazdka na końcu gry
  const [doneWordIds, setDoneWordIds] = useState<string[]>([]);

  const dropZonesRef = useRef<(HTMLDivElement | null)[]>([]);
  const { play: playAudio, stop: stopAudio } = useAudioPlayer();

  const currentWord = currentWords[wordIndex];
  const rawTerm = currentWord?.term || '';
  const targetWord = cleanTextForTTS(rawTerm);

  // Hasło podzielone na słowa do rzędu pól i same litery, które uczeń układa — pozostałe znaki stoją od początku
  // na swoim miejscu
  const { words: targetWords, letters: targetLetters } = useMemo(() => splitPhrase(targetWord), [targetWord]);

  const generateUniqueId = useCallback(() => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }, []);

  // Funkcja dodająca słowo do powtórki (wzorowana na WritingView)
  const addWordToRepeat = useCallback(() => {
    const currentWordObj = currentWords[wordIndex];
    if (!currentWordObj) return;

    const wordTerm = currentWordObj.term;

    const remainingWords = currentWords.slice(wordIndex + 1);
    const alreadyHasRepeat = remainingWords.some((word) => word.term === wordTerm);

    if (alreadyHasRepeat) {
      return;
    }

    let needsSeparator = false;
    if (wordIndex < currentWords.length - 1) {
      const nextWordTerm = currentWords[wordIndex + 1].term;
      needsSeparator = (nextWordTerm === wordTerm);
    } else {
      needsSeparator = true;
    }

    const wordsToAdd: any[] = [];

    if (needsSeparator) {
      let randomWordObj;
      let attempts = 0;
      const maxAttempts = 50;

      do {
        let randomIndex;
        if (attempts < 30) {
          const preferredRange = Math.ceil(wordsCopy.length * 0.7);
          randomIndex = Math.floor(Math.random() * preferredRange);
        } else {
          randomIndex = Math.floor(Math.random() * wordsCopy.length);
        }
        randomWordObj = wordsCopy[randomIndex];
        attempts += 1;
      } while (attempts < maxAttempts && randomWordObj.term === wordTerm);

      const randomCopy = { ...randomWordObj };
      randomCopy.index = currentWords.length + wordsToAdd.length;
      randomCopy.id = `sep_${generateUniqueId()}`;
      randomCopy.listWordId = getListWordId(randomWordObj);
      wordsToAdd.push(randomCopy);
    }

    const wordToRepeat = { ...currentWordObj };
    wordToRepeat.index = currentWords.length + wordsToAdd.length;
    wordToRepeat.id = `repeat_${generateUniqueId()}`;
    wordToRepeat.listWordId = getListWordId(currentWordObj);
    wordsToAdd.push(wordToRepeat);

    setCurrentWords((prev) => [...prev, ...wordsToAdd]);
  }, [currentWords, wordIndex, wordsCopy, generateUniqueId]);

  // Inicjalizacja liter dla nowego słowa
  const initializeLetters = useCallback(() => {
    if (!targetWord || !currentWord) return;

    const letterArray: LetterState[] = targetLetters.map((letter: string, index: number) => ({
      letter: letter.toUpperCase(),
      originalIndex: index,
      id: `${currentWord.id}-letter-${index}`,
      isPlaced: false,
      placedInSlot: null,
    }));

    const shuffled = [...letterArray].sort(() => 0.5 - Math.random());
    setLetters(shuffled);
    setIsCorrect(null);
    setShowFeedback(false);
    setFeedbackInfo('');
    setTryAgainButton(false);
    setHadIncorrectAttempt(false);
  }, [targetWord, targetLetters, currentWord]);

  // Inicjalizacja gry
  useEffect(() => {
    setIsLoading(false);
    if (hasValidData && currentWord) {
      initializeLetters();
      setGameStage(GameStage.PLAYING);
    }
  }, [hasValidData, currentWord, initializeLetters]);

  // Obsługa upuszczenia litery
  const handleLetterDrop = useCallback((letterId: string, targetSlot: number | null) => {
    setLetters((prev) => {
      const draggedLetter = prev.find((l) => l.id === letterId);
      if (!draggedLetter) return prev;

      if (targetSlot === null) {
        return prev.map((l) => (l.id === letterId
          ? { ...l, isPlaced: false, placedInSlot: null }
          : l));
      }

      const occupyingLetter = prev.find((l) => l.placedInSlot === targetSlot);
      const draggedPreviousSlot = draggedLetter.placedInSlot;

      return prev.map((l) => {
        if (l.id === letterId) {
          return { ...l, isPlaced: true, placedInSlot: targetSlot };
        }

        if (occupyingLetter && l.id === occupyingLetter.id) {
          if (draggedPreviousSlot !== null) {
            return { ...l, placedInSlot: draggedPreviousSlot };
          }

          return { ...l, isPlaced: false, placedInSlot: null };
        }

        return l;
      });
    });
  }, []);

  const setDropZoneRef = useCallback((index: number) => (el: HTMLDivElement | null) => {
    dropZonesRef.current[index] = el;
  }, []);

  const allLettersPlaced = letters.every((l) => l.isPlaced);

  // Przycisk SOUND – hasło czytane tylko na żądanie ucznia, bez autoodtwarzania
  const handleSoundClick = useCallback(() => {
    if (!targetWord) return;

    playAudio(`${BASE_API_URL}/v1/tts?q=${encodeURIComponent(targetWord)}`);
  }, [targetWord, playAudio]);

  const handleCheck = useCallback(() => {
    const placedWord = targetLetters.map((_: any, index: number | null) => {
      const placedLetter = letters.find((l) => l.placedInSlot === index);

      return placedLetter ? placedLetter.letter : '';
    }).join('');

    const correct = placedWord.toUpperCase() === targetLetters.join('').toUpperCase();

    recordAnswer(correct, currentWord?.term);

    setIsCorrect(correct);
    setShowFeedback(true);

    if (!correct) {
      setFeedbackInfo(targetWord.toUpperCase());
      setTryAgainButton(true);
      setHadIncorrectAttempt(true);
    } else {
      setFeedbackInfo(<KiddoSmiley />);
      setTryAgainButton(false);

      const doneId = getListWordId(currentWord);
      setDoneWordIds((prev) => (prev.includes(doneId) ? prev : [...prev, doneId]));

      // Jeśli było błędne podejście, dodaj słowo do powtórki
      if (hadIncorrectAttempt) {
        addWordToRepeat();
      }
    }
  }, [letters, targetWord, targetLetters, hadIncorrectAttempt, addWordToRepeat, currentWord]);

  const tryAgain = useCallback(() => {
    setFeedbackInfo('');
    setTryAgainButton(false);
    setShowFeedback(false);
    setIsCorrect(null);

    setLetters((prev) => prev.map((l) => ({
      ...l,
      isPlaced: false,
      placedInSlot: null,
    })));
  }, []);

  const handleNextWord = useCallback(() => {
    if (wordIndex < currentWords.length - 1) {
      setWordIndex((prev) => prev + 1);
      setGameStage(GameStage.PLAYING);
    } else {
      setGameStage(GameStage.GAME_END);
    }
  }, [wordIndex, currentWords.length]);

  // SKIP – pominięte słowo nie trafia do powtórki. W monitoringu aktywności to próba z wynikiem `false`
  const handleSkip = useCallback(() => {
    recordAnswer(false, currentWord?.term);
    stopAudio();
    handleNextWord();
  }, [stopAudio, handleNextWord, currentWord]);

  const repeatGame = useCallback(() => {
    const wordsCopyLocal = [...activeList.words];
    setCurrentWords(wordsCopyLocal.sort(() => 0.5 - Math.random()));
    setWordIndex(0);
    setDoneWordIds([]);
    setGameStage(GameStage.PLAYING);
  }, [activeList]);

  if (isLoading) {
    return (
      <FixWidthContent>
        <div className={classes.dragLettersLoading}>
          <Spin size="large" tip={t('games:states.loadingList')} />
        </div>
      </FixWidthContent>
    );
  }

  if (!hasValidData || currentWords.length === 0) {
    return (
      <FixWidthContent>
        <Result
          status="warning"
          title={t('games:states.noActiveList')}
          subTitle={t('games:states.chooseList')}
          extra={(
            <Button type="primary" onClick={goToHome}>
              {t('games:states.backToLists')}
            </Button>
          )}
        />
      </FixWidthContent>
    );
  }

  return (
    <>
      <FixWidthContent className={classes.dragLettersMain}>
        {gameStage !== GameStage.GAME_END && (
          <WordCounter
            current={wordIndex + 1}
            total={currentWords.length}
          />
        )}
        {gameStage === GameStage.PLAYING && (
          <div className={classes.playingStage}>
            {currentWord.image && (
              <div className={classes.wordImage}>
                <img src={currentWord.image} alt={currentWord.term} />
              </div>
            )}

            {/* Pola i litery — na telefonie trzymanym poziomo kolumna z prawej strony obrazka, z SOUND i CHECK pod
                obrazkiem (KiddoDragLettersView.module.less) */}
            <div className={classes.board}>
              {/* Słowo przechodzi do następnego rzędu w całości, spacja to odstęp między słowami */}
              <div className={classes.dropZones}>
                {targetWords.map((cells, groupIndex) => {
                  const groupKey = `group-${currentWord.id}-${groupIndex}`;

                  return (
                    <div key={groupKey} className={classes.dropZonesWord}>
                      {cells.map((cell, cellIndex) => {
                        if (cell.kind === 'fixed') {
                          const fixedKey = `fixed-${currentWord.id}-${groupIndex}-${cellIndex}`;

                          return (
                            <FixedCharZone
                              key={fixedKey}
                              char={cell.char}
                              showFeedback={showFeedback}
                              isCorrect={isCorrect !== null ? isCorrect : undefined}
                            />
                          );
                        }

                        const placedLetter = letters.find((l) => l.placedInSlot === cell.slotIndex);
                        const zoneKey = `zone-${currentWord.id}-${cell.slotIndex}`;

                        return (
                          <LetterDropZone
                            key={zoneKey}
                            ref={setDropZoneRef(cell.slotIndex)}
                            letter={placedLetter?.letter || ''}
                            showFeedback={showFeedback}
                            isCorrect={isCorrect !== null ? isCorrect : undefined}
                            index={cell.slotIndex}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              <div className={classes.draggableArea}>
                <div className={classes.draggableLetters}>
                  {letters.map((letterState) => (
                    <DraggableLetter
                      key={letterState.id}
                      id={letterState.id}
                      letter={letterState.letter}
                      placedInSlot={letterState.placedInSlot}
                      dropZonesRef={dropZonesRef}
                      onDrop={handleLetterDrop}
                    />
                  ))}
                </div>

                {showFeedback && (
                <div className={classes.feedback} aria-live="polite">{feedbackInfo}</div>
                )}
              </div>
            </div>

            {/* SOUND, a obok niego CHECK, potem NEXT albo CLEAR — w jednym rzędzie */}
            <div className={classes.controls}>
              <Button
                className={classes.soundButton}
                onClick={handleSoundClick}
                aria-label={t('games:labels.playWord')}
              >
                <Sound className={classes.btnLabel} />
                <span className={classes.btnText}>{t('games:buttons.sound')}</span>
              </Button>

              {!showFeedback && (
                <Button
                  className={classes.checkButton}
                  onClick={handleCheck}
                  disabled={!allLettersPlaced}
                >
                  <span className={classes.questionMark} aria-hidden="true">?</span>
                  {t('games:buttons.check')}
                </Button>
              )}

              {showFeedback && isCorrect && (
                <Button
                  className={classes.nextButton}
                  onClick={handleNextWord}
                >
                  <Arrow className={classes.btnLabel} />
                  <br />
                  {t('games:buttons.next')}
                </Button>
              )}

              {tryAgainButton && (
                <div className={classes.optionsContainer}>
                  <Button
                    className={classes.clearButton}
                    onClick={tryAgain}
                    aria-label={t('games:labels.clearAndRetry')}
                  >
                    <Repeat className={classes.btnLabel} />
                    <br />
                    {t('games:buttons.clear')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SKIP do poprawnej odpowiedzi – potem jest NEXT. Stoi na dole ekranu, na środku, żeby nie zachęcał
            do pomijania słów */}
        {gameStage === GameStage.PLAYING && !isCorrect && (
          <div className={classes.skipContainer}>
            <Button
              className={classes.skipButton}
              onClick={handleSkip}
              aria-label={t('games:labels.skipWord')}
            >
              <Arrow className={classes.btnLabel} />
              <br />
              {t('games:buttons.skip')}
            </Button>
          </div>
        )}

        {gameStage === GameStage.GAME_END && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} wordsDone={doneWordIds.length} kiddo />
        )}

        {gameStage !== GameStage.GAME_END && (
          <UnsplashCredits words={currentWord ? [currentWord] : []} />
        )}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && currentWord && gameStage !== GameStage.GAME_END && (
            <EditTermButton
              wordId={getListWordId(currentWord)}
              currentTerm={currentWord.term}
              currentDefinition={currentWord.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                const listWordId = getListWordId(currentWord);

                setCurrentWords((prev) => prev.map((word) => (getListWordId(word) === listWordId
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}
        </RightSideButtons>
      </FixWidthContent>
    </>
  );
});

export default withTagManager(KiddoDragLettersView);
